import * as React from "react";
import {
  useReactTable,
  getCoreRowModel,
  type SortingState,
  type PaginationState,
} from "@tanstack/react-table";
import { DataTable, DataTablePagination } from "@repo/ui";
import { createAuditLogColumns } from "./audit-log-columns";
import type { AuditLog } from "@/lib/types/inventory";
import { usePaginatedData } from "@/hooks";

export interface AuditLogTableProps {
  onView: (log: AuditLog) => void;
  filters?: Record<string, string>;
}

export function AuditLogTable({ onView, filters = {} }: AuditLogTableProps) {
  // Server-side pagination hook
  const {
    data,
    pagination: paginationInfo,
    isLoading,
    setPage,
    setPageSize,
    setSorting: setServerSorting,
    setFilters: setServerFilters,
  } = usePaginatedData<AuditLog>({
    endpoint: "/audit",
    defaultPageSize: 50,
    defaultSortBy: "action_timestamp",
    defaultSortOrder: "desc",
  });

  // Sync filters to the hook when they change
  React.useEffect(() => {
    setServerFilters(filters);
  }, [filters, setServerFilters]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 50,
    });

  const columns = React.useMemo(() => createAuditLogColumns(onView), [onView]);

  // Handle pagination changes
  React.useEffect(() => {
    setPage(pageIndex + 1);
  }, [pageIndex, setPage]);

  React.useEffect(() => {
    setPageSize(pageSize);
  }, [pageSize, setPageSize]);

  // Handle sorting changes
  React.useEffect(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      setServerSorting(sort.id, sort.desc ? "desc" : "asc");
    }
  }, [sorting, setServerSorting]);

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: paginationInfo.totalPages,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  if (isLoading && data.length === 0) {
    return <div className="text-center py-10">Loading audit logs...</div>;
  }

  return (
    <div className="space-y-4" data-testid="audit-list">
      <DataTable
        table={table}
        getRowTestId={(row) =>
          row.original.id ? `audit-row-${row.original.id}` : undefined
        }
      />
      <DataTablePagination table={table} />
    </div>
  );
}
